import  { useState, useEffect, useCallback } from "react";

const ICONS = ["🎮", "🕹️", "👾", "🎲", "🧩", "🎯", "🎨", "🎭"];

const MemoryGame = ({ onBack, isPaused }) => {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [solved, setSolved] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [moves, setMoves] = useState(0);
  const [wrong, setWrong] = useState([]);
  const [timer, setTimer] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const initializeGame = useCallback(() => {
    const duplicatedIcons = [...ICONS, ...ICONS];
    const shuffledCards = duplicatedIcons
      .sort(() => Math.random() - 0.5)
      .map((icon, index) => ({ id: index, icon }));
    setCards(shuffledCards);
    setFlipped([]);
    setSolved([]);
    setMoves(0);
    setWrong([]);
    setTimer(0);
    setIsActive(false);
  }, []);

  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  useEffect(() => {
    let interval = null;
    if (isActive && !isPaused) {
      interval = setInterval(() => {
        setTimer((time) => time + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isActive, isPaused]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleCardClick = (id) => {
    if (disabled || flipped.includes(id) || solved.includes(id)) return;
    if (!isActive) setIsActive(true);

    if (flipped.length === 0) {
      setFlipped([id]);
      return;
    }

    if (flipped.length === 1) {
      if (flipped[0] === id) return;
      
      setDisabled(true);
      setFlipped([...flipped, id]);
      setMoves(m => m + 1);

      const firstCard = cards.find((c) => c.id === flipped[0]);
      const secondCard = cards.find((c) => c.id === id);

      if (firstCard.icon === secondCard.icon) {
        setSolved(prev => {
          const nextSolved = [...prev, flipped[0], id];
          if (nextSolved.length === (ICONS.length * 2)) {
            setIsActive(false);
          }
          return nextSolved;
        });
        setFlipped([]);
        setDisabled(false);
      } else {
        setWrong([flipped[0], id]);
        setTimeout(() => {
          setFlipped([]);
          setWrong([]);
          setDisabled(false);
        }, 1000);
      }
    }
  };

  const isGameOver = solved.length === cards.length && cards.length > 0;

  return (
    <div className="memory-game-container">
      <div className="bg-particles">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${(i * 17) % 100}%`,
            top: `${(i * 23) % 100}%`,
            animationDelay: `${i * 1.5}s`,
            width: `${100 + (i * 30)}px`,
            height: `${100 + (i * 30)}px`,
          }} />
        ))}
      </div>

      <div className="game-hud">
        <div className="hud-left">
          <button onClick={onBack} className="btn-icon-back" title="Back to Hub">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <button onClick={initializeGame} className="btn-restart-round btn-restart-mobile" title="Restart Game">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
          </button>
        </div>

        <div className="hud-center">
          <div className="hud-stat-pill">
            <div className="hud-stat">
              <label>TIME</label>
              <span>{formatTime(timer)}</span>
            </div>
            <div className="hud-divider" />
            <div className="hud-stat">
              <label>MOVES</label>
              <span>{moves}</span>
            </div>
            <div className="hud-divider" />
            <div className="hud-stat">
              <label>MATCH</label>
              <span>{solved.length / 2}/{ICONS.length}</span>
            </div>
          </div>
        </div>

        <div className="hud-right">
          <button onClick={initializeGame} className="btn-restart-round btn-restart-desktop" title="Restart Game">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
          </button>
        </div>
      </div>

      <div className="memory-grid-wrapper">
        <div className="memory-grid">
          {cards.map((card) => {
            const isFlipped = flipped.includes(card.id) || solved.includes(card.id);
            const isMatched = solved.includes(card.id);
            const isWrong = wrong.includes(card.id);
            
            return (
              <div
                key={card.id}
                className={`memory-card ${isFlipped ? "flipped" : ""} ${isMatched ? "matched" : ""} ${isWrong ? "wrong" : ""}`}
                onClick={() => handleCardClick(card.id)}
              >
                <div className="card-inner">
                  <div className="card-front">
                    <div className="pattern-layer" />
                    <span className="q-mark">?</span>
                  </div>
                  <div className="card-back">
                    <div className="shine-effect" />
                    <span className="card-emoji">{card.icon}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isGameOver && (
        <div className="game-win-overlay">
          <div className="celebration-ring" />
          <div className="win-card">
            <div className="win-header">
              <div className="trophy-icon">🏆</div>
              <h2>PERFECT!</h2>
              <p>Performance: {Math.round((ICONS.length / moves) * 100)}%</p>
            </div>
            
            <div className="win-stats-grid">
              <div className="win-stat">
                <label>TIME</label>
                <span>{formatTime(timer)}</span>
              </div>
              <div className="win-stat">
                <label>MOVES</label>
                <span>{moves}</span>
              </div>
            </div>

            <button onClick={initializeGame} className="btn-play-again-premium">
              NEW ROUND
            </button>
          </div>
        </div>
      )}

      <style>{`
        :root {
          --primary-glow: #38bdf8;
          --accent-glow: #818cf8;
          --success-glow: #10b981;
          --error-glow: #ef4444;
          --bg-dark: #020617;
          --glass-bg: rgba(255, 255, 255, 0.03);
          --glass-border: rgba(255, 255, 255, 0.1);
        }

        .memory-game-container {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          background: #020617;
          color: #fff;
          overflow: hidden;
          font-family: 'Outfit', sans-serif;
          position: relative;
        }

        .bg-particles {
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          background: radial-gradient(circle, rgba(56, 189, 248, 0.05) 0%, transparent 70%);
          border-radius: 50%;
          animation: float 20s infinite linear;
        }

        @keyframes float {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, 30px) scale(1.1); }
          100% { transform: translate(0, 0) scale(1); }
        }

        .game-hud {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: rgba(15, 23, 42, 0.85);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--glass-border);
          z-index: 10;
        }

        .hud-center {
            flex: 1;
            display: flex;
            justify-content: center;
        }

        .hud-stat-pill {
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid var(--glass-border);
            padding: 0.4rem 1rem;
            border-radius: 100px;
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .hud-stat {
            display: flex;
            flex-direction: column;
            align-items: center;
            min-width: 45px;
        }

        .hud-stat label {
            font-size: 0.5rem;
            font-weight: 700;
            color: #94a3b8;
            letter-spacing: 1px;
        }

        .hud-stat span {
            font-size: 1rem;
            font-weight: 900;
            color: #f8fafc;
        }

        .hud-divider {
            width: 1px;
            height: 15px;
            background: var(--glass-border);
        }

        .btn-icon-back, .btn-restart-round {
          background: var(--glass-bg);
          border: 1px solid var(--glass-border);
          color: #fff;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-restart-round {
            background: rgba(239, 68, 68, 0.1);
            border-color: rgba(239, 68, 68, 0.2);
            color: #f87171;
        }

        .btn-icon-back:hover { border-color: var(--primary-glow); }
        .btn-restart-round:hover { background: #ef4444; color: #fff; }

        .memory-grid-wrapper {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          z-index: 1;
        }

        .memory-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          width: 100%;
          max-width: min(90vw, 450px);
          aspect-ratio: 1 / 1;
        }

        .memory-card {
          position: relative;
          perspective: 1000px;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          aspect-ratio: 1 / 1;
        }

        .card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
          transform-style: preserve-3d;
          pointer-events: none;
        }

        .memory-card.flipped .card-inner { transform: rotateY(180deg); }
        .memory-card.wrong .card-inner { animation: shake 0.5s; }

        .card-front, .card-back {
          position: absolute;
          inset: 0;
          backface-visibility: hidden;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--glass-border);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        }

        .card-front { background: linear-gradient(145deg, #1e293b, #0f172a); }
        .card-back { background: linear-gradient(135deg, var(--primary-glow), var(--accent-glow)); transform: rotateY(180deg); }

        .pattern-layer {
          position: absolute;
          inset: 0;
          opacity: 0.05;
          background-image: radial-gradient(var(--primary-glow) 1px, transparent 1px);
          background-size: 8px 8px;
        }

        .q-mark { font-size: 1.8rem; font-weight: 200; color: rgba(56, 189, 248, 0.2); }
        .card-emoji { font-size: clamp(1.2rem, 4vh, 2.5rem); }

        .memory-card.matched .card-back {
          background: linear-gradient(135deg, #059669, #10b981);
          animation: success-pop 0.5s;
        }

        @keyframes shake {
          0%, 100% { transform: rotateY(180deg) translateX(0); }
          25% { transform: rotateY(180deg) translateX(-4px); }
          75% { transform: rotateY(180deg) translateX(4px); }
        }

        @keyframes success-pop {
          0%, 100% { transform: rotateY(180deg) scale(1); }
          50% { transform: rotateY(180deg) scale(1.1); }
        }

        .game-win-overlay {
          position: fixed;
          inset: 0;
          background: rgba(2, 6, 23, 0.95);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .win-card {
          background: rgba(15, 23, 42, 0.9);
          border: 1px solid var(--glass-border);
          border-radius: 24px;
          padding: 2rem;
          text-align: center;
          width: 85%;
          max-width: 320px;
        }

        .trophy-icon { font-size: 3rem; margin-bottom: 0.5rem; }
        .win-header h2 { font-size: 1.8rem; font-weight: 900; color: var(--primary-glow); }
        .win-header p { color: #94a3b8; font-size: 0.9rem; margin-bottom: 1.5rem; }

        .win-stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem;
          margin-bottom: 2rem;
        }

        .win-stat {
          background: rgba(255, 255, 255, 0.03);
          padding: 0.75rem;
          border-radius: 12px;
        }

        .btn-play-again-premium {
          background: var(--primary-glow);
          color: #020617;
          border: none;
          padding: 1rem;
          border-radius: 12px;
          font-weight: 800;
          width: 100%;
        }
        .btn-restart-mobile {
          display: none !important;
        }

        @media (max-width: 768px) {
          .game-hud {
            padding-top: calc(max(0.75rem, env(safe-area-inset-top)) + 2px) !important;
            padding-bottom: 0.75rem !important;
          }
          .btn-restart-desktop {
            display: none !important;
          }
          .btn-restart-mobile {
            display: flex !important;
          }
          .hud-left {
            width: 88px !important;
            display: flex !important;
            gap: 8px !important;
          }
          .hud-right {
            width: 88px !important;
            display: flex !important;
            justify-content: flex-end !important;
          }
        }

        @media (max-width: 400px) {
            .hud-stat-pill { gap: 0.5rem; padding: 0.4rem 0.6rem; }
            .hud-stat { min-width: 40px; }
            .hud-stat span { font-size: 0.8rem; }
            .memory-grid { gap: 8px; }
        }
      `}</style>
    </div>
  );
};

export default MemoryGame;
